// The ECU profile is the one JSON structure that fully describes a "virtual vehicle":
// its DIDs, its DTC catalogue, and its Security Access algorithm. Author mode edits
// this object directly, and it round-trips through JSON export/import unmodified —
// so keep every field here plain data (no functions, no class instances).
import { COMMON_DIDS, DEFAULT_SECURITY_ALGORITHM, DEFAULT_TIMING } from './constants';
import { dtcCodeToBytes } from './dtc';

export const PROFILE_SCHEMA_VERSION = 1;

function makeDefaultDtcs() {
  return [
    {
      bytes: dtcCodeToBytes('P0A1F'),
      code: 'P0A1F',
      description: 'High Voltage Interlock (HVIL) Circuit Malfunction',
      initialStatus: 0x29, // testFailed | confirmedDTC | testFailedSinceLastClear
      faultActive: true, // the simulated fault condition is still present — clearing it can let it reappear
      snapshot: { did: 0xF186, label: 'Active Session at Time of Fault', value: [0x03] },
    },
    {
      bytes: dtcCodeToBytes('P0A80'),
      code: 'P0A80',
      description: 'Replace Battery Pack (State of Health Below Threshold)',
      initialStatus: 0x08, // confirmedDTC only — stored, not currently failing
      faultActive: false,
      snapshot: { did: 0xF190, label: 'VIN at Time of Fault', value: null },
    },
  ];
}

export function createDefaultProfile() {
  return {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    name: 'Generic LV/EV Body Control Module',
    description: 'A generic virtual ECU used to teach the UDS request/response flow: session control, security access, DID read/write, and DTC memory.',
    dids: COMMON_DIDS.map((d) => ({ ...d })),
    dtcs: makeDefaultDtcs(),
    security: { ...DEFAULT_SECURITY_ALGORITHM },
    timing: { ...DEFAULT_TIMING },
  };
}

export function validateProfile(profile) {
  const errors = [];
  if (!profile || typeof profile !== 'object') return ['Profile is not a JSON object.'];
  if (!Array.isArray(profile.dids)) errors.push('Profile is missing a "dids" array.');
  if (!Array.isArray(profile.dtcs)) errors.push('Profile is missing a "dtcs" array.');
  if (!profile.security || !['xor', 'add'].includes(profile.security.type)) {
    errors.push('Profile "security.type" must be "xor" or "add".');
  }
  (profile.dids || []).forEach((d, i) => {
    if (typeof d.id !== 'number' || d.id < 0 || d.id > 0xffff) errors.push(`DID #${i + 1}: "id" must be a 16-bit number.`);
    if (!['ascii', 'hex', 'numeric'].includes(d.format)) errors.push(`DID #${i + 1} (0x${(d.id || 0).toString(16)}): "format" must be ascii, hex, or numeric.`);
    if (typeof d.length !== 'number' || d.length < 1) errors.push(`DID #${i + 1} (0x${(d.id || 0).toString(16)}): "length" must be a positive number of bytes.`);
  });
  (profile.dtcs || []).forEach((d, i) => {
    if (!Array.isArray(d.bytes) || d.bytes.length !== 3) errors.push(`DTC #${i + 1}: "bytes" must be a 3-byte array.`);
    if (typeof d.code !== 'string') errors.push(`DTC #${i + 1}: missing "code" string.`);
  });
  return errors;
}

export function findDidById(profile, id) {
  return profile.dids.find((d) => d.id === id) || null;
}
